import { db, FirebaseUser } from '../../../firebase';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import type { DocumentData, QueryConstraint } from 'firebase/firestore';

import { TENANTS_COLLECTION_NAME } from './constants';
import { trackEvent } from '../server/analytics/trackEvent';
import { getAllReports } from './reports';
import { userHasAccessToTeam } from '../../utils/accessUtils';
import { Activity } from '../../types/activity';
import { PlayerId } from '../../types/player';
import { PlayerReport } from '../../types/report';
import { UserConfig } from '../../types/user';


// add an activity
export const addActivity = async (
  activity: Activity,
  tenant: string,
  currentUser: FirebaseUser,
): Promise<string | undefined> => {

  const activitiesCollectionRef = collection(db, TENANTS_COLLECTION_NAME, tenant, 'activities');

  try {
    const docRef = await addDoc(activitiesCollectionRef, activity);
    return docRef.id;
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'addActivity', errorDetails: { error } }, currentUser, 'system');
  }
};


// delete an activity
export const deleteActivity = async (
  activityId: string,
  tenant: string,
  currentUser: FirebaseUser,
): Promise<void> => {

  const activitiesCollectionRef = collection(db, TENANTS_COLLECTION_NAME, tenant, 'activities');

  try {
    await deleteDoc(doc(activitiesCollectionRef, activityId));
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'deleteActivity', errorDetails: { error } }, currentUser, 'system');
  }
};


// get all player activities that the user has access to for given player
export const getPlayerActivites = async (
  playerId: PlayerId,
  userConfig: UserConfig,
  shouldByPassAccessCheck?: boolean,
): Promise<Activity[]> => {

  const activitiesCollectionRef = collection(db, TENANTS_COLLECTION_NAME, userConfig.club, 'activities');

  const q = query(activitiesCollectionRef, where('playerId', '==', playerId));
  const querySnapshot = await getDocs(q);

  const extractData = (docs: DocumentData[]) => docs.map(doc => ({ ...doc.data(), id: doc.id }) as Activity);
  const allPlayerActivities = extractData(querySnapshot.docs);

  if (shouldByPassAccessCheck) return allPlayerActivities;

  const playerActivities: Activity[] = [];
  allPlayerActivities.forEach(activity => {
    if (userHasAccessToTeam(userConfig, activity.teamId, activity.teamIsAcademyTeam, activity.teamOwner)) {
      playerActivities.push(activity);
    }
  });

  return playerActivities;
};


// get all team activities
export const getTeamActivities = async (
  teamId: string,
  tenant: string,
): Promise<Activity[]> => {

  const activitiesCollectionRef = collection(db, TENANTS_COLLECTION_NAME, tenant, 'activities');

  const q = query(activitiesCollectionRef, where('teamId', '==', teamId));
  const querySnapshot = await getDocs(q);

  const extractData = (docs: DocumentData[]) => docs.map(doc => ({ ...doc.data(), id: doc.id }) as Activity);
  const teamActivities = extractData(querySnapshot.docs);
  teamActivities.sort((a, b) => a.date.localeCompare(b.date));

  return teamActivities;
};


// get all player activities and merge together with reports and return the complete player history that the user has access to
export const getCompletePlayerHistory = async (
  playerId: PlayerId,
  reports: PlayerReport[],
  userConfig: UserConfig,
): Promise<(Activity | PlayerReport)[]> => {

  // get all player activities
  const playerActivities = await getPlayerActivites(playerId, userConfig);

  // merge activities and reports and sort by date
  const completePlayerHistory = [...playerActivities, ...reports];
  completePlayerHistory.sort((a, b) => a.date.localeCompare(b.date));

  return completePlayerHistory;
};


// get all activities last 14 days
const getAllActivities = async (
  userConfig: UserConfig,
  earliestReportDate?: string,
): Promise<Activity[]> => {

  const activitiesCollectionRef = collection(db, TENANTS_COLLECTION_NAME, userConfig.club, 'activities');

  const queryConstraints: QueryConstraint[] = [];
  if (earliestReportDate) {
    queryConstraints.push(where('date', '>=', earliestReportDate));
  }

  const q = query(activitiesCollectionRef, ...queryConstraints);
  const querySnapshot = await getDocs(q);

  const extractData = (docs: DocumentData[]) => docs.map(doc => ({ ...doc.data(), id: doc.id }) as Activity);
  const allActivities = extractData(querySnapshot.docs);

  const activities: Activity[] = [];
  allActivities.forEach(activity => {
    if (userHasAccessToTeam(userConfig, activity.teamId, activity.teamIsAcademyTeam, activity.teamOwner)) {
      activities.push(activity);
    }
  });

  return activities;
};


// get platform history (activities + reports) that the user has access to in the last 14 days, and return the playerId of all players involved
export const getPlatformHistoryLast14Days = async (
  userConfig: UserConfig,
  currentUser: FirebaseUser,
): Promise<{ platformHistory: (Activity | PlayerReport)[], playerIds: Set<number> }> => {

  // calculate dates
  const today = new Date();
  const fourteenDaysAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 14).toISOString().substring(0, 10);

  const [reports, activities] = await Promise.all([
    getAllReports(userConfig, currentUser, fourteenDaysAgo),
    getAllActivities(userConfig, fourteenDaysAgo)
  ]);

  const platformHistory = [...reports, ...activities];
  platformHistory.sort((a, b) => a.date.localeCompare(b.date));

  const playerIds = new Set<number>();
  platformHistory.forEach(entry => {
    if (!isNaN(Number(entry.playerId))) {
      playerIds.add(Number(entry.playerId));
    }
  });

  return {
    'platformHistory': platformHistory,
    'playerIds': playerIds
  };
};


// replace player of activity (when connecting a player)
export const replacePlayerOfActivity = async (
  activityId: string,
  newPlayerId: PlayerId,
  newPlayerName: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const activityDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'activities', activityId);

  try {
    await updateDoc(activityDocRef, {
      playerId: newPlayerId,
      playerName: newPlayerName
    });
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'replacePlayerOfActivity', errorDetails: { error } }, currentUser, 'system');
  }
};


// update player name of activity
export const updatePlayerNameOfActivity = async (
  activityId: string,
  newPlayerName: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const activityDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'activities', activityId);

  try {
    await updateDoc(activityDocRef, {
      playerName: newPlayerName
    });
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updatePlayerNameOfActivity', errorDetails: { error } }, currentUser, 'system');
  }
};
